import { useRef } from 'react'

import { cn } from '@/lib/utils'

/**
 * The design's tabs are separate raised pills, not an underlined row: 282×32,
 * `radius-sm`, 32px apart, the active one `primary-strong` with white text and
 * the rest `fill`. They shrink below 282px and wrap when the row runs out of
 * room — the design never scrolls them sideways.
 *
 * Real tab semantics: one stop in the tab order, arrow keys move between the
 * tabs and select as they go (automatic activation, which is what a filter row
 * over a list wants). Pass `aria-label`, or `aria-labelledby` pointing at the
 * heading the tabs belong to, so the tablist is announced with a name.
 *
 * `activation="manual"` is for the row where selecting is expensive — the
 * cockpit's tabs are routes, so arrowing across them would navigate three
 * times and fire every screen's queries on the way. Then the arrows move
 * focus only and Enter or Space (a button's own behaviour) selects, which is
 * what the WAI-ARIA practices prescribe for exactly that case.
 */
export interface FxSegmentedTabsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  items: { value: string; label: string }[]
  active: string
  onSelect?: (value: string) => void
  /** `automatic` (the default) selects as the arrows move; `manual` only
   *  moves focus. */
  activation?: 'automatic' | 'manual'
  /**
   * Tab width in px. The DS token is 282 and that is the default, but the
   * artboards set their own per screen — 220 on the account-level screens,
   * 200 inside a template — and a row of four 282px tabs is visibly looser
   * than the design anywhere it appears.
   */
  tabWidth?: number
}

export function FxSegmentedTabs({
  items,
  active,
  onSelect,
  activation = 'automatic',
  tabWidth,
  className,
  ...props
}: FxSegmentedTabsProps) {
  const tabs = useRef<(HTMLButtonElement | null)[]>([])
  // Exactly one tab is in the tab order. When `active` names nothing in
  // `items` — a filter that has just been emptied — that is the first one,
  // because a tablist nobody can reach with the keyboard is worse than one
  // whose entry point is not the selected tab.
  const activeIndex = items.findIndex((item) => item.value === active)
  const focusIndex = activeIndex < 0 ? 0 : activeIndex

  function move(from: number, event: React.KeyboardEvent) {
    const last = items.length - 1
    let next: number
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = from === last ? 0 : from + 1
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        next = from === 0 ? last : from - 1
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = last
        break
      default:
        return
    }
    event.preventDefault()
    tabs.current[next]?.focus()
    if (activation === 'automatic') onSelect?.(items[next].value)
  }

  return (
    <div role="tablist" className={cn('flex flex-wrap gap-8', className)} {...props}>
      {items.map((item, index) => {
        const isActive = item.value === active
        return (
          <button
            key={item.value}
            ref={(node) => {
              tabs.current[index] = node
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={index === focusIndex ? 0 : -1}
            onKeyDown={(event) => move(index, event)}
            onClick={() => onSelect?.(item.value)}
            style={tabWidth ? { width: tabWidth, flexBasis: tabWidth } : undefined}
            className={cn(
              // A fixed width with `max-w-full` and flex's default shrink is the
              // design's "282px, but give way and wrap when the row is narrow".
              'h-tab max-w-full rounded-sm text-body-strong shadow-raised',
              tabWidth ? '' : 'w-tab-width',
              isActive ? 'bg-primary-strong text-on-primary' : 'bg-fill text-text',
            )}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
